import type { TierIIReportingBasis } from '../../tri-shared/types/db.ts';
import type { TierIIChemicalReportingOutcome } from '../types/reporting.ts';

/**
 * The single rule for whether a chemical gets its own report entry, before product coverage is applied. The export
 * and every screen read the result, so a saved choice can never look applied when the report ignores it.
 */
export function chemicalReportingOutcome(input: {
  exempt: boolean;
  stateRequired: boolean;
  isEHS: boolean;
  appearsInMixture: boolean;
  reportingPossiblyRequired: boolean;
  reportingUnavoidable: boolean;
  selectedBasis: TierIIReportingBasis | null;
}): TierIIChemicalReportingOutcome {
  if (input.exempt) return { reported: false, reason: 'exempt' };
  if (input.stateRequired) return { reported: true, source: 'state', basis: 'compound' };
  if (!input.reportingPossiblyRequired) return { reported: false, reason: 'belowThreshold' };
  if (input.reportingUnavoidable) {
    if (!input.appearsInMixture)
      return { reported: true, source: 'unavoidable', basis: 'compound', basisSource: 'onlyOption' };
    return input.selectedBasis
      ? { reported: true, source: 'unavoidable', basis: input.selectedBasis, basisSource: 'selected' }
      : { reported: true, source: 'unavoidable', basis: input.isEHS ? 'compound' : 'mixture', basisSource: 'default' };
  }
  // EHS thresholds are compound-only, so an EHS chemical is never optionally reportable.
  if (!input.isEHS && input.selectedBasis) return { reported: true, source: 'elected', basis: input.selectedBasis };
  return { reported: false, reason: 'notElected' };
}

/** Whole-product entries still report a chemical that no chemical route applies to. */
export function chemicalReportingOutcomeWithProductCoverage(
  outcome: TierIIChemicalReportingOutcome,
  mixtureProductCount: number,
): TierIIChemicalReportingOutcome {
  return !outcome.reported && mixtureProductCount > 0 ? { reported: true, source: 'productCoverage' } : outcome;
}

/**
 * Ranks outcomes for merging. Not-reported outcomes rank below reported ones. Among reported ones, an outcome that
 * carries the user's basis (or the lack of one) outranks a compound-only form, because the basis is shared across
 * forms and must stay visible and applied whichever form forced the report.
 */
const outcomeStrength = (outcome: TierIIChemicalReportingOutcome): number => {
  if (!outcome.reported) return { belowThreshold: 0, notElected: 1, exempt: 2 }[outcome.reason];
  switch (outcome.source) {
    case 'productCoverage':
      return 3;
    case 'elected':
      return 5;
    case 'unavoidable':
      return outcome.basisSource === 'onlyOption' ? 4 : 6;
    case 'state':
      return 7;
  }
};

/**
 * A chemical split into EHS and non-EHS forms is reported if either form is. The merged outcome keeps the form that
 * says the most about the shared basis choice, so a saved basis still applies and a missing one still needs a
 * decision. On a tie the earlier outcome wins, and callers list the EHS form first.
 */
export function chemicalReportingOutcomeMerge(
  outcomes: readonly TierIIChemicalReportingOutcome[],
): TierIIChemicalReportingOutcome {
  return outcomes.reduce((strongest, outcome) =>
    outcomeStrength(outcome) > outcomeStrength(strongest) ? outcome : strongest,
  );
}

/** The route the calculation applies to the chemical's own entry, whether or not the user chose it. */
export function chemicalReportingOutcomeBasis(outcome: TierIIChemicalReportingOutcome): TierIIReportingBasis | null {
  return outcome.reported && outcome.source !== 'productCoverage' ? outcome.basis : null;
}

/** The user's saved basis, only while the report actually uses it. */
export function chemicalUserSavedBasis(outcome: TierIIChemicalReportingOutcome): TierIIReportingBasis | null {
  if (!outcome.reported) return null;
  switch (outcome.source) {
    case 'elected':
      return outcome.basis;
    case 'unavoidable':
      return outcome.basisSource === 'selected' ? outcome.basis : null;
    case 'state':
    case 'productCoverage':
      return null;
  }
}
